import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { RefreshCw, X, Sparkles } from 'lucide-react';

export default function PWAUpdateNotifier() {
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleUpdateAvailable = (event: Event) => {
      const customEvent = event as CustomEvent<ServiceWorkerRegistration>;
      if (customEvent.detail) {
        setSwRegistration(customEvent.detail);
        setIsVisible(true);
      }
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable);
    };
  }, []);

  const handleRefresh = () => {
    if (swRegistration && swRegistration.waiting) {
      // Send message to the waiting service worker to skip waiting
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -40, scale: 0.95 }}
          className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 text-white p-4 rounded-2xl shadow-2xl border border-white/10 pointer-events-auto flex items-start gap-3.5 relative overflow-hidden"
        >
          {/* Decorative background shapes */}
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/5 rounded-full blur-xl pointer-events-none" />
          <div className="absolute -left-6 -bottom-6 w-20 h-20 bg-blue-400/10 rounded-full blur-xl pointer-events-none" />

          {/* Icon Emblem */}
          <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center shrink-0 border border-white/10 shadow-inner">
            <Sparkles className="w-5 h-5 text-yellow-300 animate-pulse" />
          </div>

          <div className="flex-1 min-w-0 pr-6">
            <h4 className="font-bold text-sm md:text-base leading-tight">
              Update Available! / नया अपडेट उपलब्ध है!
            </h4>
            <p className="text-[11px] text-blue-100 mt-1 leading-normal font-medium">
              New products, categories, and updated store pricing are ready. Click below to load them instantly!
            </p>
            
            {/* Refresh Action Button */}
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={handleRefresh}
                className="flex items-center gap-1.5 bg-white text-blue-900 hover:bg-blue-50 px-4 py-2 rounded-xl font-bold text-xs shadow-md cursor-pointer transition-all active:scale-95 shrink-0"
              >
                <RefreshCw className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: '3s' }} />
                <span>Refresh App / अपडेट करें</span>
              </button>
              
              <button
                onClick={handleDismiss}
                className="text-[11px] text-blue-200 hover:text-white px-2 py-1.5 font-semibold cursor-pointer"
              >
                Later / बाद में
              </button>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2.5 right-2.5 p-1 text-blue-200/80 hover:text-white rounded-full hover:bg-white/10 transition-colors cursor-pointer"
            aria-label="Dismiss Update Notification"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
